/**
 * App language -> BCP-47 tag, for anything handed to `Intl`.
 *
 * The app's locale codes are FOLDER names (`src/locales/{en,cen,mx,cl,co,pe,ve,za,bra,arg,cfr}`), not BCP-47
 * tags, so they cannot go to `Intl` directly: `toLocaleString('mx')` does not
 * mean Spanish (Mexico) — `mx` is a country, not a language — and `Intl`
 * silently falls back to the browser's own locale rather than erroring. That
 * prints an English month in the middle of otherwise-Spanish copy, which is a
 * translation gap no i18n gate can catch (the string never passes through
 * `t()`).
 *
 * Anything unmapped resolves to `undefined`, i.e. the browser's own locale,
 * which is the convention elsewhere in this area.
 *
 * The two configurator SPEI payment screens carry their own copies of this map,
 * inside flows that are duplicated wholesale by design; they can adopt this one
 * whenever that pair is next touched.
 */
const BCP47_BY_APP_LANGUAGE: Record<string, string> = {
  en: 'en-US',
  cen: 'en-CA',
  mx: 'es-MX',
  es: 'es-MX',
  cl: 'es-CL',
  co: 'es-CO',
  arg: 'es-AR',
  pe: 'es-PE',
  ve: 'es-VE',
  bra: 'pt-BR',
  za: 'af-ZA',
  cfr: 'fr-CA',
};

/**
 * `Object.hasOwn` rather than a plain lookup: the language can come from a
 * `?lng=` query parameter, so a value naming an `Object.prototype` member would
 * otherwise return a prototype member from a function that promises a string.
 */
export const dateLocale = (appLanguage: string): string | undefined => {
  const key = (appLanguage || '').split(/[-_]/)[0].toLowerCase();
  return Object.hasOwn(BCP47_BY_APP_LANGUAGE, key) ? BCP47_BY_APP_LANGUAGE[key] : undefined;
};
